import { NextPage } from "next";
import { useIntl } from "react-intl";
import Head from "next/head";
import { PageProps } from "@/pages/_app";
import { Layout } from "@/components/layout/Layout";
import { routes } from "@/routes/routes";
import { VehicleType } from "@/components/common/searchBlock/children/vehicleTypeSelection/VehicleTypeSelection";
import { SEO } from "@/interfaces/seo";
import { SEOWithTemplate } from "@/interfaces/seoWithTemplate";
import {
  HeroBlock,
  HeroBlockProps,
} from "@/components/common/heroBlock/HeroBlock";
import {
  CategoryDescriptionProps,
  CategoryDescriptionWithImageProps,
  FAQProps,
  SpotlightProps,
} from "@/utils/formatCategoryPage";
import { getNonEmptyString } from "@/utils/getNonEmptyString";
import { Feature, isFeatureEnabled } from "@/utils/isFeatureEnabled";
import ReviewsBlock2 from "@/components/common/reviewsBlockV1/ReviewsBlock";
import ReviewsBlock1, {
  RentACarReview,
} from "@/components/common/reviewsBlock/ReviewsBlock";
import { StructuredReviewProps } from "@/components/common/structuredData/StructuredData.interface";
import { Spotlight } from "./spotlight/Spotlight";
import { CategoryDescription } from "./categoryDescription/CategoryDescription";
import { CategoryDescriptionWithImage } from "./categoryDescriptionWithImage/CategoryDescriptionWithImage";
import { FAQ } from "./faq/FAQ";

const ReviewsBlock = ({
  name,
  agency,
  rentACarReview,
  reviewsConst,
  image,
}: StructuredReviewProps) => {
  if (!isFeatureEnabled(Feature.NEW_REVIEWS_BLOCK)) {
    return (
      <ReviewsBlock1
        name={name}
        agency={agency}
        rentACarReview={rentACarReview}
        reviewsConst={reviewsConst}
        image={image}
      />
    );
  } else {
    return (
      <ReviewsBlock2
        name={name}
        agency={agency}
        rentACarReview={rentACarReview}
        reviewsConst={reviewsConst}
        image={image}
      />
    );
  }
};

export type CategoryPageContent = {
  name: string;
  heroBlock: HeroBlockProps | undefined;
  spotlight: SpotlightProps | undefined;
  categoryDescription: CategoryDescriptionProps[] | undefined;
  categoryDescriptionWithImage: CategoryDescriptionWithImageProps[] | undefined;
  categoryDescriptionBis: CategoryDescriptionProps[] | undefined;
  faq: FAQProps | undefined;
  rentACarReview: RentACarReview | undefined;
  seo: SEO;
  seoWithTemplate: SEOWithTemplate;
};
type CategoryPageProps = PageProps &
  CategoryPageContent & {
    slug: string;
    vehicleType: VehicleType;
  };

const hostUrl = process.env.NEXT_PUBLIC_HOST_URL ?? "";

export const CategoryPage: NextPage<CategoryPageProps> = ({
  layout,
  reviewsConst,
  slug,
  vehicleType,
  name,
  heroBlock,
  spotlight,
  categoryDescription,
  categoryDescriptionWithImage,
  categoryDescriptionBis,
  faq,
  rentACarReview,
  seo,
  seoWithTemplate,
}) => {
  const intl = useIntl();
  const categoryPageTitleId =
    vehicleType === VehicleType.TRUCK
      ? "categoryPageVUTitle"
      : "categoryPageVPTitle";
  const categoryPageTypeLabel =
    vehicleType === VehicleType.TRUCK ? "rentingTrucks" : "rentingCars";
  const urlIdentifier =
    vehicleType === VehicleType.TRUCK
      ? `vehicules-utilitaires`
      : `vehicules-tourisme`;
  const defaultCanonicalRoute =
    vehicleType === VehicleType.TRUCK
      ? routes.vehiclePageTruckCategory(slug)
      : routes.vehiclePageCarCategory(slug);

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: intl.formatMessage({ id: "home" }),
                  item: hostUrl,
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: intl.formatMessage({ id: categoryPageTypeLabel }),
                  item: `${hostUrl}/${urlIdentifier}/`,
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: intl.formatMessage({ id: "renting" }, { type: name }),
                  item: `${hostUrl}/${urlIdentifier}/${slug}`,
                },
              ],
            }),
          }}
        />
      </Head>
      <Layout
        layout={layout}
        pageMeta={seo}
        title={
          seoWithTemplate.overrideTitle ??
          intl.formatMessage(
            { id: "defaultMetaTitle" },
            {
              name: getNonEmptyString(
                seoWithTemplate.valueInTemplateTitle,
                name
              ),
            }
          )
        }
        description={
          seoWithTemplate.overrideDescription ??
          intl.formatMessage(
            { id: "categoryMetaDescription" },
            {
              department: name,
            }
          )
        }
        breadcrumbs={[
          { title: intl.formatMessage({ id: "home" }), url: "/" },
          {
            title: intl.formatMessage({ id: categoryPageTypeLabel }),
            url: `/${urlIdentifier}`,
          },
          {
            title: intl.formatMessage({ id: "renting" }, { type: name }),
            url: `/${urlIdentifier}/.${slug}`,
          },
        ]}
        defaultCanonicalRoute={defaultCanonicalRoute}
      >
        <HeroBlock
          title={
            heroBlock?.title ??
            intl.formatMessage(
              { id: categoryPageTitleId },
              {
                br: () => <br />,
                category: name,
              }
            )
          }
          searchBlockInitialState={{
            vehicleType: vehicleType,
            isOneWayTrip: false,
          }}
        />
        {spotlight && <Spotlight {...spotlight} />}
        {categoryDescription?.map((cat, index) => (
          <CategoryDescription {...cat} key={index} />
        ))}
        {categoryDescriptionWithImage?.map((cat, index) => (
          <CategoryDescriptionWithImage {...cat} key={index} />
        ))}
        {categoryDescriptionBis?.map((cat, index) => (
          <CategoryDescription {...cat} key={index} />
        ))}
        {faq?.questionsAndAnswers && (
          <FAQ questionsAndAnswers={faq.questionsAndAnswers} />
        )}
        {rentACarReview && (
          <ReviewsBlock
            rentACarReview={rentACarReview}
            reviewsConst={reviewsConst}
            image={spotlight?.image}
          />
        )}
      </Layout>
    </>
  );
};
